import React from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBack, MdContentCopy, MdIosShare, MdPrint, MdQrCode2 } from 'react-icons/md';

import 'Css/device_qr_code.css';

const withContextPath = (path) => `${process.env.CONTEXT_PATH}/${path}`;

const encodeDeviceId = (deviceId) => {
  try {
    return btoa(deviceId || '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
  } catch (e) {
    return '';
  }
};

const decodeDeviceId = (encodedDeviceId) => {
  try {
    const normalized = (encodedDeviceId || '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch (e) {
    return '';
  }
};

class DeviceQrCode extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      actionMessage: '',
    };

    this.actionMessageTimer = null;
  }

  componentWillUnmount() {
    if (this.actionMessageTimer) {
      clearTimeout(this.actionMessageTimer);
    }
  }

  setActionMessage(message) {
    if (this.actionMessageTimer) {
      clearTimeout(this.actionMessageTimer);
    }

    this.setState({ actionMessage: message });
    this.actionMessageTimer = setTimeout(() => {
      this.setState({ actionMessage: '' });
    }, 2200);
  }

  getSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  getDeviceId() {
    const searchParams = this.getSearchParams();
    const encodedDeviceId = searchParams.get('device');

    if (encodedDeviceId) {
      return decodeDeviceId(encodedDeviceId);
    }

    // Fallback per eventuali vecchi link già salvati.
    return searchParams.get('id') || '';
  }

  getDeviceName() {
    const searchParams = this.getSearchParams();
    return searchParams.get('name') || 'Dispositivo';
  }

  getPublicKeypadUrl(deviceId, deviceName) {
    const contextPath = process.env.CONTEXT_PATH || '';
    const baseUrl = window.location.origin;
    const encodedDeviceId = encodeDeviceId(deviceId);
    const encodedDeviceName = encodeURIComponent(deviceName || 'Dispositivo');

    return `${baseUrl}${contextPath}/public_keypad?device=${encodeURIComponent(encodedDeviceId)}&name=${encodedDeviceName}`;
  }

  getQrCodeUrl(publicUrl) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=18&data=${encodeURIComponent(publicUrl)}`;
  }

  async copyPublicUrl(publicUrl) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
        this.setActionMessage('Link copiato');
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = publicUrl;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.setActionMessage('Link copiato');
    } catch (e) {
      this.setActionMessage('Impossibile copiare il link');
    }
  }

  getSafeQrFileName(deviceName) {
    const normalizedName = (deviceName || 'dispositivo')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9àèéìòù_-]+/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'dispositivo';

    return `qr-code-${normalizedName}.png`;
  }

  async getQrCodeBlob(qrCodeUrl) {
    const response = await fetch(qrCodeUrl, { cache: 'no-store' });

    if (!response.ok) {
      throw new Error('Impossibile generare immagine QR code');
    }

    return response.blob();
  }

  async downloadQrCodeImage(qrCodeUrl, deviceName) {
    const blob = await this.getQrCodeBlob(qrCodeUrl);
    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = this.getSafeQrFileName(deviceName);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  async shareQrCode(qrCodeUrl, deviceName) {
    try {
      const blob = await this.getQrCodeBlob(qrCodeUrl);
      const file = new File([blob], this.getSafeQrFileName(deviceName), { type: blob.type || 'image/png' });
      const shareData = {
        title: `QR code ${deviceName || 'dispositivo'}`,
        files: [file],
      };

      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
        this.setActionMessage('Immagine QR condivisa');
        return;
      }

      await this.downloadQrCodeImage(qrCodeUrl, deviceName);
      this.setActionMessage('Condivisione immagine non supportata: QR scaricato');
    } catch (e) {
      if (e && e.name === 'AbortError') {
        return;
      }

      try {
        await this.downloadQrCodeImage(qrCodeUrl, deviceName);
        this.setActionMessage('QR scaricato');
      } catch (downloadError) {
        this.setActionMessage('Impossibile condividere il QR');
      }
    }
  }

  printQrCode(qrCodeUrl, deviceName) {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=640,height=720');

    if (!printWindow) {
      window.print();
      return;
    }

    const safeDeviceName = (deviceName || 'Dispositivo').replace(/[<>&"']/g, (char) => ({
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;',
      '"': '&quot;',
      "'": '&#039;',
    }[char]));

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>QR code ${safeDeviceName}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            @page { margin: 0; }
            html, body {
              width: 100%;
              height: 100%;
              margin: 0;
              background: #fff;
            }
            body {
              display: flex;
              align-items: center;
              justify-content: center;
            }
            img {
              width: min(92vw, 170mm);
              height: auto;
              display: block;
            }
          </style>
        </head>
        <body>
          <img src="${qrCodeUrl}" alt="QR code" onload="setTimeout(function(){ window.focus(); window.print(); }, 250);" />
        </body>
      </html>
    `);
    printWindow.document.close();
  }

  render() {
    const deviceId = this.getDeviceId();
    const deviceName = this.getDeviceName();
    const publicKeypadUrl = this.getPublicKeypadUrl(deviceId, deviceName);
    const qrCodeUrl = this.getQrCodeUrl(publicKeypadUrl);

    return (
      <div id="deviceQrCodePage" className="device-qr-only-page">
        <div className="device-qr-only-card compact">
          <div className="device-qr-only-head compact">
            <Link className="device-qr-back" to={withContextPath(`punto_controllo?id=${deviceId}`)}>
              <MdArrowBack />
              Indietro
            </Link>

            <div className="device-qr-only-title compact">
              <MdQrCode2 />
              <h1>{deviceName}</h1>
            </div>
          </div>

          <div className="device-qr-code-frame large">
            <img
              src={qrCodeUrl}
              alt={`QR code ${deviceName}`}
            />
          </div>

          <div className="device-qr-actions no-print">
            <button
              type="button"
              className="device-qr-action-btn share"
              onClick={() => this.shareQrCode(qrCodeUrl, deviceName)}
            >
              <MdIosShare />
              Condividi
            </button>
            <button
              type="button"
              className="device-qr-action-btn print"
              onClick={() => this.printQrCode(qrCodeUrl, deviceName)}
            >
              <MdPrint />
              Stampa
            </button>
          </div>

          {this.state.actionMessage ?
            <div className="device-qr-action-message no-print">
              <MdContentCopy />
              {this.state.actionMessage}
            </div>
            : ''}
        </div>
      </div>
    );
  }
}

export default DeviceQrCode;
export { DeviceQrCode };
